import {
  createElement,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
  useCallback,
  useRef,
  CSSProperties,
} from 'react';
import { usePopper } from 'react-popper';
import { State } from '@popperjs/core';
import FocusLock from 'react-focus-lock';
import eventBus, { Listener } from '@sas/nova-core/eventBus';
import {
  useDesignTokens,
  renderInlineCssVars,
  DesignTokens,
  useGlobalDesignTokens,
} from '../../theme';
import { postfixTestId } from '../../util/testId';
import { createPortal } from 'react-dom';
import { PopoverRoot, usePopoverRoot } from '../../hooks/usePopoverRoot';
import { PopoverEventName } from './types';
import { useIsRTL } from '../../locale';
import { Placement, VirtualElement } from './';
import { useOnOutsidePopoverMouseDown } from './__internal__/hooks/useOnOutsidePopoverMouseDown';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { Heading } from '../Heading';
import { usePopoverManager } from './__internal__/hooks/usePopoverManager';
import { PopoverProps, PopoverRef } from './';
import { BrowserName, getBrowserName } from '../../browser-name';
import { getScrollbarSize } from '../../util/dom';
import { useModalA11YContext } from '../Modal/ModalA11YProvider';
import { useResizeObserverRef } from '../../hooks/useResizeObserver';

import cx from 'classnames';
import cn from './Popover.css.json';
import './Popover.css';

const useHtmlId = createUseHtmlId('Popover');

export const publishPopoverEvent = (eventName: PopoverEventName) => {
  eventBus.publish(eventName);
};

const subscribePopoverEvent = (eventName: PopoverEventName, listener: Listener) => {
  eventBus.subscribe(eventName, listener);
};

const unsubscribePopoverEvent = (eventName: PopoverEventName, listener: Listener) => {
  eventBus.unsubscribe(eventName, listener);
};

export const Popover = forwardRef<PopoverRef, PopoverProps>(function Popover(props, ref) {
  const [containerElement, setContainerElement] = useState<HTMLDivElement | null>(null);
  const [popoverElement, setPopoverElement] = useState<HTMLDivElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);
  const [isFocusLockDisabled, setIsFocusLockDisabled] = useState(false);
  const isRTL = useIsRTL();
  const [titleId] = useHtmlId();
  // If position is passed in use this virtual element
  const virtualElement = useMemo<VirtualElement>(
    () => ({
      // DOMRect doesn't exist in jsdom so just create an object to represent one here
      getBoundingClientRect: () => ({
        x: 0,
        y: 0,
        height: 0,
        width: 0,
        bottom: 0,
        left: 0,
        top: 0,
        right: 0,
        toJSON: () => {},
      }),
    }),
    []
  );
  const [id, setId] = useState<number | undefined>(undefined);
  const popoverManager = usePopoverManager();

  const placement = (props.placement
    ? // if we have a top-start or bottom-start placement set and RTL is on, switch it to end
      isRTL && (props.placement === 'top-start' || props.placement === 'bottom-start')
      ? props.placement.replace('start', 'end')
      : // if we have a top-end or bottom-end placement set and RTL is on, switch it to start
      isRTL && (props.placement === 'top-end' || props.placement === 'bottom-end')
      ? props.placement.replace('end', 'start')
      : // otherwise use passed in placement
        props.placement
    : // if no placement was set, default to right or left depending on RTL
    isRTL
    ? 'left'
    : 'right') as Placement;

  // See whether we're using a `referenceElementRef` to snap to, or use passed in `position` to position a virtual element to snap to instead
  let referenceElement;
  if (props.referenceElementRef?.current) {
    referenceElement = props.referenceElementRef?.current;
  } else if (props.position) {
    referenceElement = virtualElement;
  }

  const { styles, attributes, update, forceUpdate, state } = usePopper(
    referenceElement,
    popoverElement,
    {
      placement,
      modifiers: [
        { name: 'arrow', options: { element: arrowElement, padding: 5 } },
        { name: 'offset', options: { offset: [0, props.hideArrow ? 0 : 14] } },
        {
          // Prevents an issue with the auto-start and auto-end placements
          name: 'flip',
          options: {
            flipVariations: false,
          },
        },
        {
          // Adds a little padding to the edge of the window
          name: 'preventOverflow',
          options: {
            padding: { top: 10, left: 0, bottom: 10, right: 0 },
            boundary: props.popoverRoot ?? undefined,
          },
        },
        {
          name: 'computeStyles',
          options: {
            roundOffsets: false,
          },
        },
      ],
    }
  );

  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const footerRef = useRef<HTMLDivElement | null>(null);
  const popoverPaneRef = useRef<HTMLDivElement | null>(null);

  type PopoverDimensions = {
    maxHeight: string | number | undefined;
    maxWidth: string | number | undefined;
  };
  const [popoverDimensions, setPopoverDimensions] = useState<PopoverDimensions>({
    maxHeight: undefined,
    maxWidth: undefined,
  });

  const calculateDimensions = useCallback(
    (popoverState: State | null) => {
      if (!popoverState?.rects?.reference) return;

      const style = props.style as CSSProperties;
      let popoverPaneMaxHeight: CSSProperties['maxHeight'];
      let popoverPaneMaxWidth: CSSProperties['maxWidth'];

      if (!props.popoverRoot) {
        // Popper.js will automatically move things to the side with the most room based on placement
        // Need to restrict the bounds of the popover so it fits on the side with the largest space available if it would otherwise overflow the screen

        const windowHeight = window.innerHeight;
        const windowWidth = window.innerWidth;

        // We need to account for the width of the arrow if it's visible
        const arrowOffset = props.hideArrow ? 0 : 14;

        // Popover looks like it might be cut off if extending all the way to the top and bottom of the screen, add a small buffer.
        const bufferSize = 20;
        const rect = popoverState.rects.reference;
        const heightAboveReference = rect.y - bufferSize - arrowOffset;
        const heightBelowReference = windowHeight - rect.y - rect.height - bufferSize - arrowOffset;
        const headerHeight = titleRef.current?.getBoundingClientRect().height ?? 0;
        const footerHeight = footerRef.current?.getBoundingClientRect().height ?? 0;
        let availableHeight = windowHeight - bufferSize - headerHeight - footerHeight;

        const widthLeftOfReference = rect.x - arrowOffset;
        const widthRightOfReference = windowWidth - rect.x - rect.width - arrowOffset;
        let availableWidth = windowWidth;

        const placement = popoverState.options.placement;
        const orientation =
          placement.indexOf('top') !== -1 || placement.indexOf('bottom') !== -1
            ? 'vertical'
            : placement.indexOf('left') !== -1 || placement.indexOf('right') !== -1
            ? 'horizontal'
            : 'auto';

        if (orientation === 'vertical') {
          // This is in a vertical orientation, limit popover height to largest space available above or below
          availableHeight = Math.max(heightBelowReference, heightAboveReference);
        } else if (orientation === 'horizontal') {
          // This is in a horizontal orientation, limit popover width to largest space available left or right
          availableWidth = Math.max(widthLeftOfReference, widthRightOfReference);
        } else if (orientation === 'auto') {
          // For auto placement size popover to be placed on whichever side has the most room
          if (
            Math.max(widthRightOfReference, widthLeftOfReference) >=
            Math.max(heightAboveReference, heightBelowReference)
          ) {
            // There is more room horizontally, limit popover width to largest space available left or right
            availableWidth = Math.max(widthRightOfReference, widthLeftOfReference);
          } else {
            // There is more room vertically, limit popover height to largest space available above or below
            availableHeight = Math.max(heightAboveReference, heightBelowReference);
          }
        }

        // Max height and width only apply when smaller than the calculated maximums. Popover should never overflow outside of the screen.
        const styleMaxHeight = style?.maxHeight ?? '100vh';
        const styleMaxWidth = style?.maxWidth ?? '100vw';

        popoverPaneMaxHeight = `min(${availableHeight}px, ${styleMaxHeight})`;
        popoverPaneMaxWidth = `min(${availableWidth}px, ${styleMaxWidth})`;
      } else {
        // Popper.js seems to be handling placement correctly with a custom popover root
        const boudaryRect = props.popoverRoot.getBoundingClientRect();
        popoverPaneMaxHeight = style?.maxHeight ?? `${boudaryRect?.height}px`;
        popoverPaneMaxWidth = style?.maxWidth ?? `${boudaryRect?.width}px`;
      }

      setPopoverDimensions({
        maxHeight: popoverPaneMaxHeight,
        maxWidth: popoverPaneMaxWidth,
      });

      if (popoverPaneRef.current) {
        // quicker paint to reduce any flicker, if any, before next rendering
        if (typeof popoverPaneMaxHeight === 'string')
          popoverPaneRef.current.style.maxHeight = popoverPaneMaxHeight;
        if (typeof popoverPaneMaxWidth === 'string')
          popoverPaneRef.current.style.maxWidth = popoverPaneMaxWidth;
      }
    },
    [props.popoverRoot, props.hideArrow, props.style]
  );

  useImperativeHandle(ref, () => ({
    update: () => update,
    forceUpdate: () => forceUpdate,
    container: containerElement,
    state,
  }));

  const [setRootRef] = useResizeObserverRef<Element>(() => {
    update?.();
    calculateDimensions(state);
  });
  if (props.popoverRoot) {
    setRootRef(props.popoverRoot);
  }

  // Refresh our position when RTL mode changes, the child elements change, or provided `position` changes
  useEffect(() => {
    if (props.position) {
      virtualElement.getBoundingClientRect = () => props.position!;
    }
    update?.();
    calculateDimensions(state);
  }, [isRTL, props.children, props.position, update, virtualElement, state, calculateDimensions]);

  const dt = useDesignTokens();
  const globalDesignTokens = useGlobalDesignTokens();
  const overrideTokens = useMemo(() => {
    const userTokens = props.theme;
    const overrides = userTokens ? userTokens() : undefined;
    return { ...props.font, ...overrides };
  }, [props.font, props.theme]);

  // In Firefox the scrollbar is applied inside the padding, causing it to overlay things, this adds extra padding to fix it when the scrollbar is visible
  const [paddingInlineEnd, setPaddingInlineEnd] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (containerElement && paddingInlineEnd === undefined) {
      const currentPaddingInlineEnd = window
        .getComputedStyle(containerElement)
        .getPropertyValue('padding-inline-end');
      if (
        getBrowserName() === BrowserName.Firefox &&
        containerElement?.scrollHeight > containerElement.offsetHeight
      ) {
        const offset =
          -containerElement.offsetWidth + containerElement.scrollWidth + getScrollbarSize() + 2;
        offset > 0
          ? setPaddingInlineEnd(`calc(${currentPaddingInlineEnd} + ${offset}px)`)
          : setPaddingInlineEnd(currentPaddingInlineEnd);
      } else {
        setPaddingInlineEnd(currentPaddingInlineEnd);
      }
    }
  }, [containerElement, paddingInlineEnd]);

  const popoverPaneTokens = {
    ...(dt.enableNovaTokens ? dt.backgroundsDepthNova.medium : dt.backgroundsDepth.medium),
    bd: dt.c.colorUiBorder,
    padding: dt.spacing.core.m,
    'border-radius': '0.125rem',
    'font-size-m': dt.fontNova.size.m,
    'title-font-weight': dt.fontNova.weight.thick,
    'title-fg': dt.c.colorTextPrimary,
  };

  const popoverStyle = renderInlineCssVars({ ...popoverPaneTokens, ...overrideTokens }, 'popover');

  const defaultPopoverRoot = usePopoverRoot();
  const popoverRoot: PopoverRoot = props.popoverRoot ?? defaultPopoverRoot;

  // This closes on unmount and when onOpen changes to false to handle both onOpen and conditional rendering. Make sure close only gets triggered once.
  const closeTriggered = useRef(false);

  // Tell PopoverManager when this popover opens so that it can assign it a key/group/level, and set the id it returns here
  useEffect(() => {
    if (id === -1) {
      for (const popover of popoverManager.activePopovers.values()) {
        if (
          popover.referenceElementRef === props.referenceElementRef &&
          popover.popoverElement === popoverElement
        ) {
          return setId(popover.id);
        }
      }
    }

    // Should only do this once to get back an id
    if (!popoverElement || id !== undefined) return;

    const returnId = popoverManager.openPopover({
      popoverElement,
      referenceElementRef: props.referenceElementRef,
    });
    closeTriggered.current = false;

    // PopoverManager will return the id of this popover
    setId(returnId);
  }, [props.referenceElementRef, props.position, id, popoverElement, popoverManager]);

  const cleanupPopover = (id: number) => {
    setId(undefined);
    closeTriggered.current = true;
    popoverManagerRef.current.closePopover(id);
    setContainerElement(null);
    setPopoverDimensions({ maxHeight: undefined, maxWidth: undefined });
    setPaddingInlineEnd(undefined);
  };

  // Tell PopoverManager when this popover closes
  // We only want this happening on dismount, not every time values coming from PopoverManager change, so this ref is used to keep popoverManger out of the dependency array
  const popoverManagerRef = useRef(popoverManager);
  popoverManagerRef.current = popoverManager;
  useEffect(() => {
    return () => {
      if (id !== undefined && id > -1 && popoverManagerRef.current && !closeTriggered.current) {
        cleanupPopover(id);
      }
    };
  }, [id]);

  // Also want to notify PopoverManager when isOpen becomes false
  useEffect(() => {
    if (
      id !== undefined &&
      id > -1 &&
      popoverManagerRef.current &&
      !closeTriggered.current &&
      props.isOpen === false
    ) {
      cleanupPopover(id);
    }
  }, [id, props.isOpen]);

  // This checks if a mouse click fell outside of our popover content, or the content of any of its child popovers
  useOnOutsidePopoverMouseDown(
    props.isModal
      ? undefined
      : () => {
          props.onClose?.();
          props.onOutsideClick?.();
        },
    popoverElement,
    props.referenceElementRef?.current,
    popoverManager.activePopovers,
    id
  );

  const onClose = props.onClose;
  // Used to close all instances of popover when resize or scroll occurs
  const onClosePopover = useCallback(
    (event: MouseEvent | Event | UIEvent | WheelEvent) => {
      const target = event.target as HTMLElement;
      // Don't do anything if popover is closed, `onClose` is unhandled, or this is a modal popover
      if (props.isOpen === false || !onClose || props.isModal) return;

      if (event instanceof MouseEvent || event instanceof WheelEvent) {
        const allMenuContent = document.getElementsByClassName(cn.popover);
        for (const content of allMenuContent) {
          if (content && content.contains(target)) {
            return;
          }
        }
      }

      onClose();
    },
    [onClose, props.isOpen, props.isModal]
  );

  useEffect(() => {
    // Listen to the resize action to close the popover.
    window.addEventListener('resize', onClosePopover);

    // Listen to the wheel action to close the popover.
    window.addEventListener('wheel', onClosePopover);
    // this line comes from a basic scroll hook called useDisableScroll.
    // The relevant stackoverflow(https://stackoverflow.com/questions/4770025/how-to-disable-scrolling-temporarily)
    // giving the possible cause of the above line is that 'This code will work on Chrome 56 and further'.
    document.addEventListener('wheel', onClosePopover);

    return () => {
      window.removeEventListener('resize', onClosePopover);
      window.removeEventListener('wheel', onClosePopover);
      document.removeEventListener('wheel', onClosePopover);
    };
  }, [onClosePopover]);

  // handle custom event handlers
  useEffect(() => {
    if (!props.isModal && props.isOpen !== false) {
      const disableFocusLockNonModal = () => {
        setIsFocusLockDisabled(true);
      };

      const closePopover = () => {
        onClose?.();
      };

      subscribePopoverEvent('popover:disableFocusLockNonModal', disableFocusLockNonModal);
      subscribePopoverEvent('popover:onCloseNonModal', closePopover);

      return () => {
        unsubscribePopoverEvent('popover:disableFocusLockNonModal', disableFocusLockNonModal);
        unsubscribePopoverEvent('popover:onCloseNonModal', closePopover);
      };
    }
  }, [props.isModal, props.isOpen, onClose]);

  // Applies aria-hidden to an element that wraps the base application ui layer. Prevents Accessibility issues
  const publishOpenState = useModalA11YContext();

  useEffect(() => {
    if (props.isOpen && props.isModal) {
      publishOpenState(true);
    }
    return () => {
      publishOpenState(false);
    };
  }, [props.isOpen, publishOpenState, props.isModal]);

  const arrowStyle = renderInlineCssVars(getPopoverArrowTokens(dt), 'popover-arrow');

  const content = (
    /* By default this container will use the title id for aria-labelledby */
    <div
      id={props.id}
      data-testid={props.testId}
      ref={setPopoverElement}
      style={{ ...styles.popper, ...popoverStyle }}
      {...attributes.popper}
      className={cn.popover}
      role={props.role}
      aria-label={props.ariaLabel}
      aria-labelledby={props.ariaLabelledby ?? (props.title ? titleId : undefined)}
      aria-describedby={props.ariaDescribedby}
      onKeyDown={(e) => {
        setIsFocusLockDisabled(false);
        if (e.key === 'Escape') {
          props.onClose?.();
          props.onEscape?.();
          // if `referenceElementRef` exists and `returnFocusOnEscape` isn't false then send focus back to it
          if (props.referenceElementRef && props.returnFocusOnEscape !== false) {
            setTimeout(() => props.referenceElementRef?.current?.focus(), 0);
          }
          // This prevents an issue where closing a popover nested inside another component that closes on Escape, such as Settings or PropertyPaneLayout, would close both
          e.stopPropagation();
        }
        if (e.key === ' ') {
          // This prevents an issue where the space button would toggle checkboxes outside of this popover (Checkbox: WithLabel story was an example of this when using the HelpPopup)
          e.stopPropagation();
        }
      }}
      onClick={(e) => e.stopPropagation()}
      {...(!props.isModal && {
        onMouseDown: () => setIsFocusLockDisabled(false),
      })}
      onContextMenu={(e) => {
        if (!props.allowOnContextMenuPropagation) {
          e.stopPropagation();
        }
      }}
    >
      <div
        role={!props.onClose && !props.isModal ? 'region' : undefined}
        data-landmark-label={
          !props.onClose && !props.isModal ? props.landmarkLabel || props.title : undefined
        }
        className={cn.popoverInner}
        onContextMenu={(e) => (props.disableContextMenu ? e.preventDefault() : undefined)}
      >
        {/* If a title is passed in, display it and set main container to be aria-labelledby it */}
        {props.title ? (
          <Heading
            id={titleId}
            testId={postfixTestId(props.testId, 'title')}
            level={props.isModal ? 1 : 2}
            style={props.titleStyle}
            className={cx(cn.popoverTitle, props.titleClassName)}
            ref={titleRef}
          >
            {props.title}
          </Heading>
        ) : null}
        <div
          tabIndex={props.focusable ? 0 : undefined}
          data-testid={postfixTestId(props.testId, 'content')}
          className={cx(
            cn.popoverPane,
            props.className,
            !!props.title && cn.hasTitle,
            !!props.footer && cn.hasFooter
          )}
          style={{
            ...props.style,
            maxHeight: popoverDimensions.maxHeight,
            maxWidth: popoverDimensions.maxWidth,
            paddingInlineEnd,
          }}
          ref={(ref) => {
            if (ref && popoverDimensions.maxHeight === undefined) {
              calculateDimensions(state);
            }
            // Wait for dimension calculation so any parents get correct size of this popover
            if (ref && state && popoverDimensions.maxHeight !== undefined) {
              setContainerElement(ref);
            }
            popoverPaneRef.current = ref;
          }}
        >
          {props.children}
        </div>
        {props.footer ? (
          <div
            ref={footerRef}
            data-testid={postfixTestId(props.testId, 'footer')}
            className={cn.popoverFooter}
          >
            {props.footer}
          </div>
        ) : null}
      </div>
      {props.hideArrow ? null : (
        <div
          {...attributes.arrow}
          data-testid={postfixTestId(props.testId, 'arrow')}
          ref={setArrowElement}
          style={{ ...styles.arrow, ...arrowStyle }}
          className={cn.arrow}
        />
      )}
    </div>
  );

  if (
    popoverRoot === null ||
    props.isOpen === false ||
    // Popover can't render if neither a `referenceElementRef` or `position` are provided
    (!props.referenceElementRef && !props.position)
  ) {
    return null;
  }
  return createPortal(
    <div
      className={cx(cn.viewportOverlay, !props.isModal ? cn.blockPointerEvents : null)}
      style={globalDesignTokens}
      onMouseDown={(event) => {
        if (props.isModal && event.currentTarget === event.target) {
          // prevent focus change when the overlay is clicked
          event.preventDefault();
        }
      }}
    >
      <FocusLock
        {...(!props.isModal && { disabled: isFocusLockDisabled })}
        {...props.focusLockProps}
      >
        {content}
      </FocusLock>
    </div>,
    popoverRoot
  );
});

const getPopoverArrowTokens = (dt: DesignTokens) => {
  if (dt.enableNovaTokens) {
    return { bd: dt.c.colorUiBorder };
  } else {
    switch (dt.themeVariant) {
      case 'dark':
        return { bd: dt.c.p2 };
      case 'high-contrast':
        return { bd: dt.c.white };
      default:
        return { bd: dt.c.n3 };
    }
  }
};
